import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { tagApi, tagQueryKeys } from "../api/tag";
import {
  CrmAssignableTag,
  CrmTag,
  CrmTagPayload,
  CrmTagStatus,
  CrmTagTarget,
  GetCrmTagsParams,
} from "types/crm/tag";

/**
 * The tag library, as Settings → Tags lists it.
 *
 * Search and status are the API's to apply, not the page's: the library is
 * paginated, so filtering the current page locally would quietly hide matches
 * sitting on the next one.
 */
export const useCrmTags = (
  params: GetCrmTagsParams = {},
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: tagQueryKeys.list(params),
    queryFn: () => tagApi.getTags(params),
    enabled: options?.enabled ?? true,
    // Keeps the previous page on screen while a new search settles, rather
    // than flashing the empty state between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

/**
 * The tags a Contact or Company form may offer — Active, and applicable to
 * that record type.
 *
 * Cached longer than the settings list: these only move when an admin edits
 * the library, and every tag write invalidates `all`, which covers this too.
 */
export const useAssignableTags = (
  recordType: CrmTagTarget,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: tagQueryKeys.assignable(recordType),
    queryFn: (): Promise<CrmAssignableTag[]> =>
      tagApi.getAssignableTags(recordType),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
    // A refusal (403) or bad request (4xx) gives the same answer the second
    // time, so retrying only keeps the field waiting before it can say so.
    // Network failures and 5xx get the one retry they might recover from.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      const isClientError = status !== undefined && status >= 400 && status < 500;
      return !isClientError && failureCount < 1;
    },
  });

/**
 * Where a tag is currently used, for the usage view.
 *
 * Never cached: the spec is explicit that this always reflects the records as
 * they are now, and it is read on demand when someone opens the view — so a
 * stale answer has no upside. `enabled` keeps it from firing until then.
 */
export const useCrmTagUsage = (
  tagUuid: string | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: tagQueryKeys.usage(tagUuid ?? ""),
    queryFn: () => tagApi.getTagUsage(tagUuid as string),
    enabled: !!tagUuid && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 0,
  });

/**
 * Every write invalidates the whole list: a create, a rename and a status
 * change can each move a row between the All / Active / Inactive views and
 * between pages, so no single cached page can be patched honestly.
 *
 * A 404 invalidates too — it means another admin deleted the tag out from under
 * this screen, and the row the user is acting on should stop being there.
 *
 * Errors are otherwise deliberately not toasted here: the duplicate-name
 * message belongs inside the form that caused it. Call sites read
 * `mutation.error.message`.
 */
const useTagWriteCallbacks = () => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: tagQueryKeys.all });

  return {
    onSuccess: invalidate,
    onError: (error: unknown) => {
      if ((error as { status?: number } | null)?.status === 404) invalidate();
    },
  };
};

export const useCreateCrmTagMutation = () => {
  const callbacks = useTagWriteCallbacks();

  return useMutation({
    mutationFn: (payload: CrmTagPayload): Promise<CrmTag> =>
      tagApi.createTag(payload),
    ...callbacks,
  });
};

export const useUpdateCrmTagMutation = () => {
  const callbacks = useTagWriteCallbacks();

  return useMutation({
    mutationFn: ({
      tagUuid,
      ...payload
    }: CrmTagPayload & { tagUuid: string }): Promise<CrmTag> =>
      tagApi.updateTag(tagUuid, payload),
    ...callbacks,
  });
};

export const useUpdateCrmTagStatusMutation = () => {
  const callbacks = useTagWriteCallbacks();

  return useMutation({
    mutationFn: ({
      tagUuid,
      status,
    }: {
      tagUuid: string;
      status: CrmTagStatus;
    }): Promise<CrmTag> => tagApi.updateTagStatus(tagUuid, status),
    ...callbacks,
  });
};

export const useDeleteCrmTagMutation = () => {
  const callbacks = useTagWriteCallbacks();

  return useMutation({
    mutationFn: (tagUuid: string): Promise<void> => tagApi.deleteTag(tagUuid),
    ...callbacks,
  });
};
